/**
 * Showroom Visits (renamed from "Test Drive Leads" in iter139 F —
 * results-first restructure). Copy leads with the outcome: people who
 * physically walked in.
 *
 * Dealer-facing conversion funnel: how many people SIGNED UP vs how
 * many actually came in and REDEEMED. NOT a delivery-status console.
 *
 * Rationale for the redesign (per user spec):
 *  - Delivery-status is an operator problem, not a dealer one. Failed
 *    rows made the platform look broken and the dealer can't act on
 *    them. Delivery issues live in the master Stranded Codes panel.
 *  - Codes are never visible to dealers.
 *  - is_test rows are excluded from both counters.
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { RefreshCw, Loader2, Users, CheckCircle2 } from "lucide-react";

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString(undefined, {
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return dt;
  }
}

export default function DealerRedemptionLog() {
  const { apiFetch } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ signed_up: 0, redeemed: 0, test_rows_excluded: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await apiFetch("/dealer/redemption-log");
      setRows(r?.data?.rows || []);
      setSummary(
        r?.data?.summary || { signed_up: 0, redeemed: 0, test_rows_excluded: 0 }
      );
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-6 max-w-5xl" data-testid="dealer-redemption-log">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Showroom Visits</h1>
          <p className="text-slate-600 text-sm mt-1">
            How many customers signed up through your Test Drive popup and how many actually
            walked in and redeemed. Your conversion funnel from popup → foot traffic.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="dealer-log-refresh">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <Card data-testid="dealer-log-summary-signed-up">
          <CardContent className="py-4">
            <div className="text-[11px] uppercase text-slate-500 font-semibold tracking-wider flex items-center gap-1.5">
              <Users size={12} /> Signed up
            </div>
            <div className="text-3xl font-bold text-slate-900 mt-1">{summary.signed_up || 0}</div>
            <div className="text-[11px] text-slate-500 mt-1">
              People who booked through your popup
            </div>
          </CardContent>
        </Card>
        <Card data-testid="dealer-log-summary-redeemed">
          <CardContent className="py-4">
            <div className="text-[11px] uppercase text-slate-500 font-semibold tracking-wider flex items-center gap-1.5">
              <CheckCircle2 size={12} /> Redeemed
            </div>
            <div className="text-3xl font-bold text-emerald-700 mt-1">{summary.redeemed || 0}</div>
            <div className="text-[11px] text-slate-500 mt-1">
              Came in and claimed their card
            </div>
          </CardContent>
        </Card>
      </div>
      {summary.test_rows_excluded > 0 && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
          {summary.test_rows_excluded} test-mode row{summary.test_rows_excluded === 1 ? "" : "s"} shown below but excluded from these counts.
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
        <CardContent className="p-0">
          {err && <div className="p-4 text-red-800 bg-red-50 border-t border-red-200">{err}</div>}
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="dealer-log-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr className="text-left">
                  <th className="px-4 py-2 font-semibold">Customer</th>
                  <th className="px-4 py-2 font-semibold">Phone</th>
                  <th className="px-4 py-2 font-semibold">Date</th>
                  <th className="px-4 py-2 font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                      No test-drive leads yet.
                    </td>
                  </tr>
                )}
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    className={`hover:bg-slate-50 ${r.is_test ? "opacity-70" : ""}`}
                    data-testid={`dealer-log-row-${i}`}
                  >
                    <td className="px-4 py-2 font-medium text-slate-900">
                      {r.customer_name || "—"}
                      {r.is_test && (
                        <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                          TEST
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-slate-600">{r.customer_phone || "—"}</td>
                    <td className="px-4 py-2 text-slate-600">{fmt(r.date)}</td>
                    <td className="px-4 py-2 text-slate-800">${r.amount_dollars || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
